import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Leaf, Phone, Clock, Calendar, CheckCircle2, XCircle, RefreshCw } from "lucide-react";

export default function InterviewConfirm() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [verifyEmail, setVerifyEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/portal/interview/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "Invitation not found"));
  }, [token]);

  const submit = async (action) => {
    if (action === "confirm" && !selectedSlot) return toast.error("Please pick a time slot");
    if (!verifyEmail.trim()) return toast.error("Please confirm your email so we know it's you");
    setBusy(true);
    try {
      const body = { action, verify_email: verifyEmail.trim() };
      if (action === "confirm") body.slot = selectedSlot;
      const { data: res } = await api.post(`/portal/interview/${token}/confirm`, body);
      setDone({ action, status: res.status });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  if (error) return (
    <Wrap>
      <div className="cc-card p-10 text-center">
        <XCircle className="w-12 h-12 mx-auto mb-4 text-[var(--cc-accent)]" />
        <h2 className="font-display text-2xl font-bold mb-2">Link not found</h2>
        <p className="text-[var(--cc-text-soft)]">{error}</p>
        <Link to="/jobs" className="btn-secondary mt-5 inline-flex">Browse open roles</Link>
      </div>
    </Wrap>
  );

  if (!data) return <Wrap><div className="text-center text-[var(--cc-text-soft)] py-12">Loading…</div></Wrap>;

  if (done) return (
    <Wrap>
      <div className="cc-card p-10 text-center fade-up" data-testid="confirm-success">
        <CheckCircle2 className="w-14 h-14 mx-auto mb-5 text-[var(--cc-success)]" />
        <h2 className="font-display text-3xl font-bold mb-2">
          {done.action === "confirm" ? "Interview confirmed" : done.action === "decline" ? "Interview declined" : "Request received"}
        </h2>
        <p className="text-[var(--cc-text-soft)] mb-6">
          {done.action === "confirm" && `We've sent you a confirmation email and SMS. We'll be in touch on ${new Date(selectedSlot).toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}.`}
          {done.action === "decline" && "We're sorry to hear you can no longer attend. Our team has been notified."}
          {done.action === "suggest" && "Our recruiter will be in touch shortly to find a time that works for you."}
        </p>
        <Link to="/jobs" className="btn-secondary">Browse more roles</Link>
      </div>
    </Wrap>
  );

  if (data.status === "scheduled" && data.selected_slot) {
    const when = new Date(data.selected_slot);
    return (
      <Wrap>
        <div className="cc-card p-10 text-center" data-testid="already-confirmed">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-[var(--cc-success)]" />
          <h2 className="font-display text-2xl font-bold mb-2">You're confirmed for {when.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</h2>
          <p className="text-[var(--cc-text-soft)] mb-4">at {when.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} ({data.duration_minutes} min phone interview)</p>
          <p className="text-sm text-[var(--cc-text-soft)]">Need to reschedule? Reply to your confirmation email.</p>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="fade-up">
        <div className="cc-card p-8 mb-4" data-testid="interview-confirm-card">
          <div className="label-eyebrow mb-2">Phone screen invitation</div>
          <h1 className="font-display text-3xl font-bold mb-1">Hi {data.candidate_name.split(" ")[0]} 👋</h1>
          <p className="text-[var(--cc-text-soft)] mb-5">You've been invited to a phone interview for <strong>{data.job_title}</strong>. Please choose a time that works for you.</p>
          <div className="flex flex-wrap gap-4 text-sm text-[var(--cc-text-soft)] mb-4">
            <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> Phone call</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {data.duration_minutes} minutes</span>
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {(data.offered_slots || []).length} options</span>
          </div>
          {data.instructions && <div className="p-3 rounded-lg bg-[var(--cc-surface)] text-sm">{data.instructions}</div>}
        </div>

        <div className="space-y-2 mb-5">
          {(data.offered_slots || []).map((s) => {
            const d = new Date(s);
            const sel = selectedSlot === s;
            return (
              <button key={s}
                onClick={() => setSelectedSlot(s)}
                className={`cc-card w-full p-4 text-left flex items-center justify-between hover:border-[var(--cc-primary)] transition ${sel ? "ring-2 ring-[var(--cc-primary)]" : ""}`}
                data-testid={`slot-${s}`}
              >
                <div>
                  <div className="font-display font-bold text-lg">{d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</div>
                  <div className="text-sm text-[var(--cc-text-soft)]">{d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
                {sel && <CheckCircle2 className="w-6 h-6 text-[var(--cc-primary)]" />}
              </button>
            );
          })}
        </div>

        <div className="cc-card p-4 mb-4" data-testid="verify-identity-card">
          <label className="label-eyebrow block mb-1">Confirm your email so we know it's you</label>
          <input
            type="email" autoComplete="email"
            className="cc-input"
            value={verifyEmail}
            onChange={(e) => setVerifyEmail(e.target.value)}
            placeholder="the email this invitation was sent to"
            data-testid="verify-email-input"
          />
          <p className="text-[11px] text-[var(--cc-text-soft)] mt-2">
            For your protection, please re-enter the email address this invitation was sent to. Your response will be logged with your IP for our audit trail.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          <button className="btn-primary flex-1 justify-center" disabled={!selectedSlot || busy} onClick={() => submit("confirm")} data-testid="confirm-slot-button">
            <CheckCircle2 className="w-4 h-4" /> {busy ? "Confirming…" : "Confirm this time"}
          </button>
          <button className="btn-secondary" disabled={busy} onClick={() => submit("suggest")} data-testid="suggest-other-time-button">
            <RefreshCw className="w-4 h-4" /> None work — suggest other times
          </button>
          <button className="btn-ghost text-[var(--cc-accent)]" disabled={busy} onClick={() => submit("decline")} data-testid="decline-button">
            Decline
          </button>
        </div>
      </div>
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div className="min-h-screen" style={{ background: "var(--cc-bg)" }}>
      <header className="cc-topbar px-8 py-4 flex items-center">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--cc-primary)" }}>
            <Leaf className="w-4 h-4 text-white" />
          </div>
          <div className="font-display font-bold text-lg">CareConnect</div>
        </Link>
      </header>
      <div className="max-w-2xl mx-auto px-6 py-12">{children}</div>
    </div>
  );
}
